import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MarkAsPaidDialogBuilder } from './mark-as-paid-dialog-builder';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import {
  CompanySettings,
  ManualPaymentReceiptsEnabledEnum,
} from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { Document } from '../../../document-data/document.model';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { EmailService } from 'i2g-ng-utils';
import { BannerCanvasTypesEnum } from '../../../features-data/features-data.data';
import { get } from 'lodash';
import { Billing } from '../../../document-data/sub-models/billing.model';
import {
  IInvalidEmailConfirmDialogData,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { fromJS, List } from 'immutable';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  I2gNgTrackingActionBuilder,
  ITrackingAction,
  RadioTappedTrackingAction,
} from 'i2g-ng-tracking';
import { AnimationService } from 'i2g-ng-ui';
import markAsPaidAnimationData from '../../../../../assets/json/mark-as-paid.json';

export const DIALOG_TYPE = 'mark-as-paid-dialog';

@Component({
  selector: 'app-mark-as-paid-dialog',
  templateUrl: './mark-as-paid-dialog.component.html',
  styleUrls: ['./mark-as-paid-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MarkAsPaidDialogBuilder],
})
export class MarkAsPaidDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountId: string;
  @Input() companySettings: CompanySettings;
  @Input() invalidEmailDialogId: string;
  @Input() items: List<any>;
  @Output() confirmMarkAsPaid: EventEmitter<any> = new EventEmitter<any>();
  @Output() onGrowthCta: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  sendPreviewReceipt: EventEmitter<ISendReceiptPreview> = new EventEmitter<ISendReceiptPreview>();
  @ViewChild('animationRef', { static: true }) animationRef: ElementRef;
  private _validEmail: boolean;
  private _focusableTrigger: BehaviorSubject<any> = new BehaviorSubject<any>(
    true
  );
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  dialogType: string;
  paymentReceiptsEnabled: boolean;
  paymentReceiptsOn: boolean;
  markAsPaidAmount: number;
  formKeys: any;
  featureHighlightIcon: IIcon;
  markAsPaidIcon: IIcon;
  document: Document;
  form: FormGroup;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject: ITrackingObject;
  confirmMarkAsPaidAction: ITrackingAction;
  undoAction: ITrackingAction;
  sendPreviewAction: ITrackingAction;
  saveNewMethodAction: ITrackingAction;
  addNoteAction: ITrackingAction;
  defaultMethodAction: RadioTappedTrackingAction;
  buttonActionAdditionalProperties: any;
  toggleActionAdditionalProperties: any;
  defaultPaymentMethods: string[];
  cachedPaymentMethods: string[] = [];
  focusableTrigger$: Observable<void> = this._focusableTrigger.asObservable();
  markAsPaidAnimation: any;

  constructor(
    private _markAsPaidDialogBuilder: MarkAsPaidDialogBuilder,
    private _dialogService: DialogService,
    private _featuresDataStore: FeaturesDataStore,
    private _emailService: EmailService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _animationService: AnimationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.companySettings && this.companySettings) {
      this.paymentReceiptsOn =
        this.companySettings.manualPaymentReceiptsEnabled ===
        ManualPaymentReceiptsEnabledEnum.on;
    }
  }

  ngOnInit() {
    const paymentReceiptsFeatureName = Toggles.KEYS.PAYMENT_RECEIPTS;
    this.dialogType = DIALOG_TYPE;
    this.defaultPaymentMethods = this._markAsPaidDialogBuilder.buildDefaultMethods();
    this.formKeys = this._markAsPaidDialogBuilder.FORM_KEYS;

    this.trackingScreenName = TrackingScreenNamesEnum.mark_as_paid;
    this.confirmMarkAsPaidAction = this._trackingActionBuilder.buildButtonTapped(
      'done',
      this.trackingScreenName
    );
    this.undoAction = this._trackingActionBuilder.buildButtonTapped(
      'undo',
      this.trackingScreenName
    );
    this.saveNewMethodAction = this._trackingActionBuilder.buildButtonTapped(
      'save',
      this.trackingScreenName
    );
    this.defaultMethodAction = this._trackingActionBuilder.buildRadioTapped(
      'method',
      this.trackingScreenName
    );
    this.sendPreviewAction = this._trackingActionBuilder.buildButtonTapped(
      'preview_receipt',
      this.trackingScreenName
    );
    this.addNoteAction = this._trackingActionBuilder.buildButtonTapped(
      'payment_notes',
      this.trackingScreenName
    );

    this.markAsPaidIcon = {
      name: 'green-checkmark',
      size: IconSizesEnum.custom,
    };

    // animation loaded
    this.markAsPaidAnimation = this._animationService.loadAnimation({
      container: this.animationRef.nativeElement,
      name: 'mark-as-paid-animation',
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData: markAsPaidAnimationData as any,
    });

    this._featuresDataStore
      .hasFeatureHighlightCanvas(paymentReceiptsFeatureName)
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x)
      )
      .subscribe(() => {
        this.featureHighlightIcon = {
          name: 'premium-feature',
          size: IconSizesEnum.medium,
          customClass: 'premium-icon',
        };
      });

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === this.dialogType && action === DialogActionsEnum.refresh
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this._changeDetectorRef.markForCheck());

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ type, action }: IDialogTrigger) =>
            type === this.dialogType && action === DialogActionsEnum.open
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(({ data }: IDialogTrigger) => {
        const { document, documentBilling, calculationResult } = data;

        if (document) {
          this.document = document;
        }
        if (documentBilling) {
          this._validEmail =
            documentBilling &&
            this._emailService.validateEmails(documentBilling.email);
        }
        this.markAsPaidAmount = calculationResult
          ? calculationResult.outstandingBalance
          : 0;
        this.trackingObject = this._trackingObjectBuilder.buildDocument(
          document.id,
          document.localId,
          document.docNumber,
          document.overallState,
          document.revisionId,
          document.documentType
        );

        // play the animation each time dialog opens
        this._animationService.playAnimation(
          this.markAsPaidAnimation,
          [0, 60],
          true
        );

        const paymentReceiptsAllAccess$ = this._featuresDataStore.isAllAccess(
          paymentReceiptsFeatureName
        );
        paymentReceiptsAllAccess$
          .pipe(takeUntil(this._destroyed))
          .subscribe((isPaymentReceiptsAllAccess: boolean) => {
            this.paymentReceiptsEnabled =
              this.paymentReceiptsOn &&
              isPaymentReceiptsAllAccess &&
              this._validEmail;
            this.form = this._markAsPaidDialogBuilder.buildForm(
              this.paymentReceiptsEnabled
            );
          });

        this.buttonActionAdditionalProperties = {
          variant_mark_as_paid: true,
        };

        this.toggleActionAdditionalProperties = {
          variant_mark_as_paid: true,
          toggle_value: this.paymentReceiptsEnabled,
        };

        combineLatest(
          this.sendReceiptToggle.valueChanges,
          paymentReceiptsAllAccess$
        )
          .pipe(takeUntil(this._destroyed))
          .subscribe(([value, allAccess]) => {
            if (!allAccess || !this._validEmail) {
              this.sendReceiptToggle.setValue(!value, { emitEvent: false });

              if (!allAccess) {
                this.showFeatureLockDialog();
              } else {
                const email = get(
                  this.document.billing.email,
                  Billing.KEYS.EMAIL,
                  ''
                );

                if (!email || !this._emailService.validateEmails(email)) {
                  this._dialogService.openConfirmDialog(
                    this.invalidEmailDialogId,
                    {
                      documentType: this.document.documentType,
                      documentId: this.document.id,
                    } as IInvalidEmailConfirmDialogData
                  );
                }
              }
            } else {
              this.toggleActionAdditionalProperties = {
                variant_mark_as_paid: true,
                toggle_value: value,
              };

              this._changeDetectorRef.markForCheck();
              this._changeDetectorRef.detectChanges();
            }
          });
      });
  }

  get paymentMethodRadio() {
    return this.form.get(this.formKeys.PAYMENT_METHODS_RADIO);
  }

  get customMethodRadio() {
    return this.form.get(this.formKeys.CUSTOM_METHOD_RADIO);
  }

  get sendReceiptToggle() {
    return this.form.get(this.formKeys.SEND_RECEIPT_TOGGLE);
  }

  get addNoteTextarea() {
    return this.form.get(this.formKeys.ADD_NOTE_TEXTAREA);
  }

  closeDialog() {
    this.form.reset();
    this._dialogService.closeDialog({ type: this.dialogType });
  }

  onSubmit() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        const renderingOptions = this._markAsPaidDialogBuilder.buildRenderingOptions(
          this.companySettings.payments,
          featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
          featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
          featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
        );
        const payload = this._markAsPaidDialogBuilder.buildConfirmMarkAsPaidPayload(
          this.paymentMethodRadio.value,
          this.addNoteTextarea.value,
          this.sendReceiptToggle.value,
          renderingOptions
        );
        this.confirmMarkAsPaid.emit({
          documentId: this.document.id,
          payload: payload,
        });
      });
    this.customMethodRadio.setValue('');
  }

  showFeatureLockDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      Toggles.KEYS.PAYMENT_RECEIPTS,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  showFeatureHighlightDialog() {
    this._dialogService.openGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      Toggles.KEYS.PAYMENT_RECEIPTS,
      this.onGrowthCta.emit.bind(this.onGrowthCta)
    );
  }

  onPreviewReceipt() {
    this._featuresDataStore
      .getFeatureToggles()
      .pipe(takeUntil(this._destroyed))
      .subscribe((featureToggles: Toggles) => {
        const renderingOptions = this._markAsPaidDialogBuilder.buildRenderingOptions(
          this.companySettings.payments,
          featureToggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT),
          featureToggles.isAllAccess(Toggles.KEYS.CARD_PAYMENTS),
          featureToggles.isAllAccess(Toggles.KEYS.PAYPAL_EC)
        );
        const payload = this._markAsPaidDialogBuilder.buildSendReceiptPreview(
          fromJS(this.form.value),
          this.dialogType,
          renderingOptions,
          this.document
        );
        this.sendPreviewReceipt.emit(payload);
      });
  }

  ngOnDestroy() {
    const paymentReceiptsFeatureName = Toggles.KEYS.PAYMENT_RECEIPTS;

    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_highlight,
      paymentReceiptsFeatureName
    );
    this._dialogService.closeGrowthDialog(
      BannerCanvasTypesEnum.feature_lock,
      paymentReceiptsFeatureName
    );
    this._dialogService.closeConfirmDialog(this.invalidEmailDialogId);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
